import mondaySdk from "monday-sdk-js";
import "monday-ui-react-core/dist/main.css";
// Semantic colour tokens, layered over Vibe's primitives. Every colour in the
// app resolves through these — see the header of tokens.css for why.
import "./styles/tokens.css";
import React from "react";
import "./App.css";
//Explore more Monday React Components here: https://style.monday.com/
import {
  Activity,
  AlertCircle,
  FolderOpen,
  Globe,
  Plus,
  RefreshCw,
  Users
} from 'lucide-react';
import { Dropdown } from "monday-ui-react-core";
// Vibe icons for the template actions — Wand reads as "generate this for me",
// which is what Apply does. Kept as one set so the action row is consistent.
import {
  CloseSmall,
  Delete,
  Doc,
  Duplicate,
  Edit,
  ExternalPage,
  Help,
  Wand,
  Launch,
} from "monday-ui-react-core/icons";
import ApplyMappingDialog from "./components/ApplyMappingDialog";
import ApplyResultAlert from "./components/ApplyResultAlert";
import { applyErrorResult, groupApplyResult, groupProgressText, groupRunActive, subitemApplyResult } from "./lib/applyResult";
import AppUsageView from "./components/AppUsageView";
import GroupTemplateEditor, { GroupTemplateView } from "./components/GroupTemplateEditor";
import LayoutSkeleton from "./components/LayoutSkeleton";
import BoardSubitemsTable from "./components/SubitemsTable";
import TemplateEditor from "./components/TemplateEditor";
import { NewViewBanner, ViewHowTo } from "./components/ViewIntro";
import { resolveColumns } from "./lib/columnMatching";
import { duplicateDraft } from "./lib/duplicateTemplate";
import mondayApi from "./lib/mondayApi";
import { fetchSubitemsBoard, fetchSubitemsBoardOfBoard } from "./lib/subitemsBoard";
import { fetchAccountPeople, knownMentionKeys } from "./lib/accountPeople";
import { accountSlug, applyGroupTemplate, hasSubitemsColumn } from "./lib/applyGroup";
import { refusedFields } from "./lib/captureGroup";
import { applyBaseDate, resolveSubitemsForApply } from "./lib/dynamicValues";
import { countContent } from "./lib/groupContent";
import { templateType } from "./lib/templateLimits";
import { authorIds, authorNameOf, fetchUserNames, formatUpdatedAt } from "./lib/templateMeta";
import { takenNamesFor } from "./lib/templateNames";
import { applyMondayTheme } from "./lib/theme";
import { fetchBoardById } from "./lib/useItemBoardColumns";
import {
  CREATE_UPDATE_MUTATION,
  countUpdates,
  serializeUpdate,
} from "./lib/updates";

const Backend = require('./Backend');
const Config = require('./Config');

/**
 * The two kinds of template, one per monday view: an item view lists subitem
 * templates (they fill that item), a board view lists group templates (they
 * add a group to that board). A stored row without `type` is a subitem
 * template (older rows). `this.state.templates` holds every template the
 * backend returns; the view decides which are listed.
 */
const TEMPLATE_KINDS = [
  { kind: 'subitem', label: 'Subitem templates', hint: 'Select a template to preview its subitems, then apply it to this item.' },
  { kind: 'group', label: 'Group templates', hint: 'Select a template to preview the group it adds to this board.' },
];

/** The template kind a monday view works with: group templates in a board view, subitem templates elsewhere. */
export const kindForView = (context) => (context && context.instanceType === 'board_view' ? 'group' : 'subitem');

// monday-sdk-js stays for the host-side calls the new SDK does not cover —
// get('sessionToken'), listen('context'), execute('notice' | 'confirm').
// Every GraphQL call goes through lib/mondayApi (SeamlessApiClient).
const monday = mondaySdk();

/**
 * Single source of truth for the docs link. It used to be inlined in the help
 * card's JSX; naming it here means the URL is referenced by name at the point of
 * use and only has to change in one place when the docs move.
 */
const USER_GUIDE_URL =
  'https://docs.beelabxapps.com/docs/apps/subitem-pro-templates/user-guide';

class App extends React.Component {
  constructor(props) {
    super(props);

    // Default state
    this.state = {
      context: {},
      loading: true,
      applyingTemplate: false,
      // counts, not a percentage: the bar is derived from these, so it cannot top
      // out below 100% the way `applyProgress` did (it was set BEFORE each row,
      // so a 4-row apply never got past 75%).
      applyDone: 0,
      applyTotal: 0,
      creatingColuns: false,
      deletingTemplate: false, // spinner on the Delete button, not a page takeover
      // Help popover starts OPEN; the ✕ collapses it to the round launcher.
      // Session-only on purpose — see the note on renderHelpBubble() about
      // persisting this per user if reopening on every item view gets annoying.
      helpOpen: true,
      // which screen of the popover: 'home', or 'howto' (adding the other view — ViewIntro)
      helpStep: 'home',
      // The screen the guided tour is on (lib/tours), null when no tour runs.
      // The help bubble hides for the tour's duration; the tour's own state —
      // the selection to restore, whether it opened the editor — lives on the
      // instance (this.tourSaved, this.tourOpenedEditor), not in render state.
      tourScreen: null,
      applyMapping: null,
      // the group apply run: { stage, done, total, groupId?, result?, error?, templateName }
      groupRun: null, // a running group apply: { templateName, stage, done, total } for the overlay
      applyResult: null, // how the last apply ended: the alert on the selected template (lib/applyResult)
      // App Usage is a MODE of the one shell, not a route: the navbar button
      // toggles it and everything below the navbar swaps. Nothing else is torn
      // down, so coming back lands on the same template list.
      usageOpen: false,
      editorOpen: false,
      editorTemplate: null,
      subitems: [],
      columns: [],

      templates: [],
      authorNames: {}, // { [monday user id]: name } for the templates' authors (lib/templateMeta)
      selectedTemplate: null, // { value: index into visibleTemplates() }
      contextReady: false, // the first context names the view, and so the kind listed
      editorKind: 'subitem', // which editor editorOpen renders
      sessionToken: null,
      noBoardError: false,
    };

    // Original initialization logic
    monday.get('sessionToken').then((res) => {
      this.setState({ sessionToken: res.data });
      Backend.getTemplates(res.data).then((templates) => {
        this.setState({ loading: false, templates });
        this.loadAuthorNames(templates);
      });
    });
  }

  componentDidMount() {
    monday.listen('context', this.getContext);
  }

  getContext = (res) => {
    const context = res.data;
    // monday's theme is the ONLY colour signal — all four of its themes map to
    // a Vibe class, and nothing else (least of all the OS setting) gets a vote.
    applyMondayTheme(context.theme);
    // a context naming another view (it never does mid-session) starts the list over
    const viewChanged = this.state.contextReady && kindForView(context) !== kindForView(this.state.context);
    this.setState(viewChanged ? { context, selectedTemplate: null, applyResult: null } : { context, contextReady: true });
  };

  /** The kind of template this view lists and creates (kindForView). */
  templateKind = () => kindForView(this.state.context);

  getSubitemsBoard = async (itemID) => {
    // Shared with the Template Editor — see lib/subitemsBoard for the
    // create-dummy / read / delete bootstrap on an item with no subitems.
    try {
      const { board } = await fetchSubitemsBoard(mondayApi, itemID);
      return board;
    } catch (e) {
      console.error('Could not reach the subitems board', e);
      this.setState({ noBoardError: true });
      return null;
    }
  };

  /** Reads the names of authors not yet known; a refusal leaves the list without them. */
  loadAuthorNames = async (templates) => {
    if (!Array.isArray(templates)) return;
    const missing = authorIds(templates).filter((id) => !(id in this.state.authorNames));
    if (!missing.length) return;
    const names = await fetchUserNames(mondayApi, missing);
    this.setState((prev) => ({ authorNames: { ...prev.authorNames, ...names } }));
  };

  /** A dropdown row: the template name, its author muted on the right. */
  renderTemplateOption = ({ label, author }) => (
    <div className="flex items-center justify-between w-full min-w-0 gap-3">
      <span className="truncate">{label}</span>
      {author && <span className="flex-none text-xs text-secondary">{author}</span>}
    </div>
  );

  /** The templates of this view's kind, in list order; selectedTemplate.value indexes this. */
  visibleTemplates = () => this.state.templates.filter((t) => templateType(t) === this.templateKind());

  /** The template the dropdown has selected, or undefined. */
  selected = () => (this.state.selectedTemplate ? this.visibleTemplates()[this.state.selectedTemplate.value] : undefined);

  /** The dropdown option for the visible template at `index` — what templateChanged stores — or null. */
  templateOption = (index) => {
    const t = this.visibleTemplates()[index];
    return t ? { label: t.name, value: index, author: authorNameOf(t, this.state.authorNames) } : null;
  };

  /**
   * Create flow → the editor of this view's kind (starts from an EMPTY
   * table; nothing is read from the current item unless the user syncs it — REQ-5).
   */
  createNewTemplate = () => {
    this.setState({ editorOpen: true, editorTemplate: null, editorKind: this.templateKind() });
  };

  handleEditorSave = async (payload) => {
    const result = await Backend.saveTemplate(this.state.sessionToken, payload);
    // A refused save (over a limit, see lib/templateLimits) comes back as
    // { error } instead of the list: say why and keep the editor open.
    if (!Array.isArray(result)) {
      monday.execute('notice', {
        message: (result && result.error) || 'The template could not be saved.',
        type: 'error',
        timeout: Config.NOTIFICATION_TIMEOUT,
      });
      return null;
    }
    this.loadAuthorNames(result);
    this.setState({
      templates: result,
      editorOpen: false,
      editorTemplate: null,
      selectedTemplate: null,
    });
    return result;
  };

  handleEditorCancel = () => {
    this.setState({ editorOpen: false, editorTemplate: null });
  };

  /** Edit flow → the editor of the template's kind, prefilled from the stored template (REQ-4). */
  editTemplate = () => {
    const template = this.selected();
    this.setState({ editorOpen: true, editorTemplate: template, editorKind: templateType(template) });
  };

  /**
   * Duplicate flow (REQ-8) → same unified editor, prefilled with a COPY that has
   * no id, so Save inserts a new template instead of updating this one. Nothing
   * is written until the user saves, so Cancel is a true no-op and the original
   * template is never touched.
   */
  duplicateTemplate = () => {
    const template = this.selected();
    const draft = duplicateDraft(
      template,
      this.state.templates.map((t) => t.name),
    );
    this.setState({ editorOpen: true, editorTemplate: draft, editorKind: templateType(template) });
  };

  openUsage = () => this.setState({ usageOpen: true });

  /** Passed to AppUsageView as onBack — its "Back to Templates" link. */
  closeUsage = () => this.setState({ usageOpen: false });

  /**
   * Persistent top navbar — rendered in EVERY mode (list, editor and App Usage),
   * so the app never blanks when one of them opens. Identity on the left, name in
   * bold with the tagline under it; the App Usage toggle on the right.
   *
   * The row spans the FULL bar width — no max-width, no mx-auto. It used to carry
   * max-w-7xl, which was invisible while the row held only the identity block but
   * parked the toggle at the 1280px mark on a wider viewport, floating in the
   * middle of the bar instead of sitting at its end. The bar is chrome: it tracks
   * the iframe's edges, not the centred content column below it.
   */
  renderNavbar = () => (
    <header className="bg-surface border-b border-subtle">
      <div className="flex items-center gap-3 px-6 py-3">
        {/*
          Served from public/, not imported, so the official exported asset can be
          dropped in over public/app-icon.png with no code change. alt="" because
          the app name sits right next to it — a screen reader would just repeat it.
        */}
        <img
          src={`${process.env.PUBLIC_URL}/app-icon.png`}
          alt=""
          width="36"
          height="36"
          className="flex-none rounded-md"
        />
        <div className="min-w-0">
          <h1 className="font-bold leading-tight text-primary truncate">Subitem Pro Templates</h1>
          <p className="text-sm leading-snug text-secondary truncate">
            Create, manage, and apply templates to streamline your workflow
          </p>
        </div>

        {this.renderUsageTab()}
      </div>
    </header>
  );

  /**
   * "App Usage" — the one nav control, pushed to the end of the bar by ml-auto.
   *
   * ONE appearance, in every state. It used to carry a filled accent while the
   * view was open, which made the bar's only control flicker between two looks
   * on a bar that is otherwise static chrome. The view now owns the way back
   * (AppUsageView's "Back to Templates" link, same as the editor's), so the
   * button has one job — open it — and pressing it again is simply a no-op.
   *
   * Disabled while the editor is open: swapping the content out would unmount
   * TemplateEditor and take an unsaved draft with it, so the bar stays visible
   * but the door is shut until the user saves or cancels (same reasoning as the
   * help bubble).
   *
   * Hidden entirely for view-only users, who are blocked from the app one level
   * down in renderContent() and would only reach the same refusal from here.
   *
   * The focus ring is --spt-accent-hover, NOT --spt-accent. Not a design choice:
   * every token bridged through an -rgb triple (accent, surface, page, primary,
   * danger) currently computes to transparent, because tokens.css writes those
   * triples comma-separated while Tailwind emits `rgb(var(--…-rgb) / <alpha>)` —
   * comma syntax with a slash alpha is invalid, so the declaration is dropped.
   * --spt-accent-hover is a plain var and paints. Once the triples are fixed to
   * space-separated, this can go back to ring-accent.
   */
  renderUsageTab = () => {
    const { context, editorOpen } = this.state;
    if (context.user && context.user.isViewOnly) return null;

    return (
      <button
        type="button"
        onClick={this.openUsage}
        disabled={editorOpen}
        title={
          editorOpen
            ? 'Save or cancel the template first'
            : 'View your Operation Counter usage'
        }
        className="flex-none inline-flex items-center gap-2 px-3 py-2 ml-auto text-sm font-medium transition-colors border rounded-md text-primary bg-surface border-strong hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Activity className="w-4 h-4" />
        App Usage
      </button>
    );
  };

  /**
   * The help bubble belongs to the template list only. It stays out of the way of
   * the editor (nothing should float over an unsaved form), App Usage, the apply
   * progress and the first-load skeleton, and it has nothing to offer on a
   * blocking screen the user can't act on anyway.
   */
  showHelpBubble = () => {
    const { context, loading, editorOpen, usageOpen, noBoardError, applyingTemplate, tourScreen } =
      this.state;
    if (context.user && context.user.isViewOnly) return false;
    // During the tour the bubble would sit over the bottom-right of the page —
    // right where Apply and Save are highlighted — so it stays hidden until the
    // last step, which points at its "Take a tour" button.
    if (tourScreen && tourScreen !== 'done') return false;
    // applyingTemplate is listed explicitly now: the apply used to set `loading`
    // too, so this rule got the "not during apply" case for free. It no longer
    // does, and the bubble must not float over the scrim.
    //
    // usageOpen for the same reason as editorOpen: the bubble is about templates
    // and has nothing to say over an embedded dashboard it would only obscure.
    return !editorOpen && !usageOpen && !loading && !noBoardError && !applyingTemplate;
  };

  closeHelp = () => this.setState({ helpOpen: false });

  openHelp = () => this.setState({ helpOpen: true, helpStep: 'home' });

  showViewHowTo = () => this.setState({ helpStep: 'howto' });

  backToHelpHome = () => this.setState({ helpStep: 'home' });

  /**
   * The tour's code — driver.js, its stylesheet and the step scripts — is not
   * part of the first load: it lives in its own chunk (webpackChunkName "tour",
   * shared by both imports) that the browser fetches the first time it is
   * needed. Hovering or focusing the button warms it, so the click itself
   * does not wait on the network.
   */
  loadTour = () =>
    Promise.all([
      import(/* webpackChunkName: "tour" */ './lib/tourRunner'),
      import(/* webpackChunkName: "tour" */ './lib/tours'),
    ]).then(([{ runTour }, { buildTour }]) => ({ runTour, buildTour }));

  /**
   * "Take a tour": the tour of this view's kind (lib/tours), driven by
   * lib/tourRunner. The selection is remembered so the tour can select a
   * template to point at and put things back when it ends.
   */
  startTour = async () => {
    // already running (its overlay makes the button unreachable anyway), or
    // a double click while the chunk is still on its way
    if (this.tour || this.tourLoading) return;
    this.tourLoading = true;
    let tour;
    try {
      tour = await this.loadTour();
    } catch (e) {
      console.error('Could not load the tour', e);
      monday.execute('notice', { message: 'The tour could not be loaded. Check your connection and try again.', type: 'error', timeout: Config.NOTIFICATION_TIMEOUT });
      return;
    } finally {
      this.tourLoading = false;
    }
    this.tourSaved = this.state.selectedTemplate;
    this.tourOpenedEditor = false;
    this.setState({ tourScreen: 'list' });
    const steps = tour.buildTour(this.templateKind(), { hasTemplates: this.visibleTemplates().length > 0 });
    this.tour = tour.runTour(steps, this.tourGo);
  };

  /**
   * The tour asks for a screen before it moves to a step that lives there
   * (see lib/tours for the screens). This is the ONLY code the tour runs
   * against the app's state, so everything it can change is listed here, and
   * 'restore' undoes all of it: the editor it opened closes (a blank draft,
   * so no "Discard changes?"), the selection goes back to what it was.
   */
  tourGo = (screen) => {
    const closeEditor = () => {
      if (this.state.editorOpen && this.tourOpenedEditor) {
        this.tourOpenedEditor = false;
        this.setState({ editorOpen: false, editorTemplate: null });
      }
    };
    switch (screen) {
      case 'list':
        closeEditor();
        break;
      case 'list-selected':
        closeEditor();
        if (!this.state.selectedTemplate) this.setState({ selectedTemplate: this.templateOption(0), applyResult: null });
        break;
      case 'list-empty':
        closeEditor();
        this.setState({ selectedTemplate: null });
        break;
      case 'editor':
        if (!this.state.editorOpen) {
          this.tourOpenedEditor = true;
          this.createNewTemplate();
        }
        break;
      case 'done':
        // the popover's home screen, where the "Take a tour" button is
        closeEditor();
        this.setState({ selectedTemplate: this.tourSaved, helpOpen: true, helpStep: 'home' });
        break;
      case 'restore':
        closeEditor();
        this.setState({ selectedTemplate: this.tourSaved, tourScreen: null });
        this.tour = null;
        return;
      default:
        break;
    }
    this.setState({ tourScreen: screen });
  };

  /**
   * Getting started — a bottom-right popover above a round launcher, replacing the
   * old "Documentation & Support" card and its Action Guide grid. The grid described
   * what Edit/Duplicate/Apply/Delete do, which the buttons' own labels and tooltips
   * already cover and the tour will cover properly; what's left is the two things a
   * new user actually needs, so it no longer earns a slot in the page flow.
   *
   * Open on mount, ✕ collapses it to the launcher. That state is SESSION-ONLY: a
   * user who opens ten items sees it ten times. To make dismissal stick, persist a
   * flag (monday.storage per user, or the backend) and seed `helpOpen` from it in
   * the constructor — this is the only place that would need to change.
   *
   * `fixed` positions against the iframe viewport, not the content column, so the
   * popover stays put while the page behind it scrolls.
   */
  renderHelpBubble = () => {
    if (!this.state.helpOpen) {
      return (
        <button
          type="button"
          onClick={this.openHelp}
          title="Getting started"
          aria-label="Open getting started"
          className="fixed z-40 flex items-center justify-center text-on-accent transition-transform bg-accent rounded-full shadow-lg bottom-5 right-5 w-11 h-11 hover:bg-accent-hover hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
        >
          <Help size={20} />
        </button>
      );
    }

    return (
      <div
        role="dialog"
        aria-label="Getting started"
        className="fixed z-40 bg-surface border border-subtle shadow-xl bottom-5 right-5 w-80 rounded-xl"
      >
        <button
          type="button"
          onClick={this.closeHelp}
          title="Close"
          aria-label="Close getting started"
          className="absolute flex items-center justify-center text-secondary/70 rounded-md top-2.5 right-2.5 w-7 h-7 hover:bg-hover hover:text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
        >
          <CloseSmall size={18} />
        </button>

        {this.state.helpStep === 'howto' ? (
          <ViewHowTo onBack={this.backToHelpHome} guideUrl={USER_GUIDE_URL} />
        ) : (
          <div className="px-5 pt-5 pb-4">
            {/* item view only: group templates, announced; its button swaps in ViewHowTo.
                A board view already is the group-template view, so it shows Getting started alone. */}
            {this.templateKind() === 'subitem' && <NewViewBanner onShowHow={this.showViewHowTo} />}
            <h4 className="mb-1.5 pr-7 font-semibold text-primary">Getting started</h4>
            <p className="mb-4 text-sm leading-relaxed text-secondary">
              Take the guided tour for a quick walkthrough, or read the full documentation
              for column matching, access levels and troubleshooting.
            </p>

            {/* Stacked full-width so the popover stays narrow enough not to crowd the page. */}
            <div className="flex flex-col gap-2.5">
              {/* data-tour: the tour's last step points back at this button */}
              <button
                type="button"
                onClick={this.startTour}
                onMouseEnter={this.loadTour}
                onFocus={this.loadTour}
                data-tour="tour-btn"
                title="A one-minute walkthrough of templates and the editor"
                className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-primary bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
              >
                <Launch size={16} className="mr-2" />
                Take a tour
              </button>

              {/*
                ExternalPage sits AFTER the label as the "this leaves the app" marker,
                so Doc can lead the row the same way Launch leads the tour button and the
                two rows line up. rel="noreferrer" because of target="_blank".
              */}
              <a
                href={USER_GUIDE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-primary bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
              >
                <Doc size={16} className="mr-2" />
                User guide (v2.4)
                <ExternalPage size={14} className="ml-2 text-secondary/70" />
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Selected template: ONE card holding its identity and its subitems, with Apply
   * as a page-level footer below the card — the same place the editor puts
   * Cancel/Save, so the primary action sits in a consistent spot across screens.
   */
  renderTemplateSelected = () => {
    let template = this.selected();
    const isGroup = templateType(template) === 'group';
    const groupCounts = isGroup ? countContent(template.content) : null;
    const author = authorNameOf(template, this.state.authorNames);
    const updatedAt = formatUpdatedAt(template.updated_at);
    const deleting = this.state.deletingTemplate;
    const groupRunning = groupRunActive(this.state.groupRun);
    const applying = this.state.applyingTemplate || groupRunning;
    const busy = deleting || applying;
    return (
      <>
        {/* relative wrapper so the apply scrim covers exactly this card */}
        <div className="relative mb-6">
        <div className="p-6 space-y-6 bg-surface border border-subtle rounded-lg shadow-sm">
        {/* How the last apply ended, where its progress was shown */}
        <ApplyResultAlert result={this.state.applyResult} onClose={() => this.setState({ applyResult: null })} />
        {/* Template info — plain name + subheading, actions on the right */}
        <div className="flex items-start justify-between gap-4">
          <div data-tour="card-header">
            <h3 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-lg font-semibold text-primary">
              {template.name}
              {/* who can see it, as a plain tag beside the name */}
              <span className="inline-flex items-center text-sm font-normal text-secondary">
                {template.access_level === 'user_level' ? (
                  <>
                    <Users className="w-3.5 h-3.5 mr-1.5" />
                    User level
                  </>
                ) : (
                  <>
                    <Globe className="w-3.5 h-3.5 mr-1.5" />
                    Account level
                  </>
                )}
              </span>
            </h3>
            {(author || updatedAt) && (
              <p className="mt-0.5 text-sm text-secondary">
                {author && (
                  <>
                    By <span className="font-medium text-primary">{author}</span>
                  </>
                )}
                {author && updatedAt && <span className="mx-2 text-secondary/70">·</span>}
                {updatedAt && (
                  <time dateTime={template.updated_at} title={new Date(template.updated_at).toString()}>
                    Updated {updatedAt}
                  </time>
                )}
              </p>
            )}
            <div className="flex items-center mt-1 text-sm text-secondary">
              <span>
                {isGroup ? (
                  `1 group · ${groupCounts.items} items · ${groupCounts.subitems} subitems · ${groupCounts.updates} updates`
                ) : (
                  <>
                    {template.subitems?.length || 0} subitems · {template.columns?.length || 0} columns
                    {countUpdates(template.subitems) > 0 && ` · ${countUpdates(template.subitems)} updates`}
                  </>
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-none gap-3" data-tour="actions">
            <button
              onClick={this.duplicateTemplate}
              disabled={busy}
              title={`Open a copy named "${template.name} copy" — nothing is saved until you save it`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Duplicate size={16} className="mr-2" />
              Duplicate
            </button>

            <button
              onClick={this.editTemplate}
              disabled={busy}
              title="Change name, access level, columns, values and order — one screen, one save"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Edit size={16} className="mr-2" />
              Edit
            </button>

            <button
              onClick={this.deleteTemplate}
              disabled={busy}
              aria-busy={deleting}
              title="Remove template permanently (cannot be undone)"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-danger-ink bg-danger-tint border border-danger rounded-md shadow-sm hover:bg-danger-disc focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger transition-colors disabled:opacity-70 disabled:cursor-wait"
            >
              {deleting ? (
                <RefreshCw className="w-4 h-4 mr-2 text-danger-ink animate-spin" />
              ) : (
                <Delete size={16} className="mr-2 text-danger-ink" />
              )}
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>

        {/* The template's content, monday-board style, read-only */}
        <div className="w-full" style={{maxHeight: '420px', overflowY: 'auto'}} data-tour="preview">
          {isGroup ? (
            <GroupTemplateView content={template.content} />
          ) : (
            <BoardSubitemsTable
              columns={template.columns}
              subitems={template.subitems}
              readOnly
            />
          )}
        </div>

        </div>
          {applying && this.renderApplyScrim()}
        </div>

        {/* Apply — page-level footer, mirroring the editor's Save position */}
        <div className="flex justify-end">
          {/*
            While applying, the button is only a muted label — the spinner and the
            progress live in the scrim, so a second spinner here would be two
            animations for one operation.
          */}
          <button
            onClick={this.applyTemplate}
            data-tour="apply"
            disabled={busy}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-on-accent bg-accent border border-transparent rounded-md shadow-sm hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              applying
                ? isGroup
                  ? 'Adding this group to the board…'
                  : 'Applying this template to the current item…'
                : isGroup
                  ? 'Add this group, its items and subitems to the bottom of this board'
                  : 'Generate template for the current item immediately'
            }
          >
            {!applying && <Wand size={16} className="mr-2" />}
            {applying ? 'Applying…' : 'Apply Template'}
          </button>
        </div>
      </>
    );
  };

  /**
   * Create the columns the user chose "create" for in the mapping dialog.
   * Returns idMap extended with the new column ids.
   */
  createColumnsForDecisions = async (board, decisions, idMap) => {
    const toCreate = decisions.filter((d) => d.action === 'create');
    if (!toCreate.length) return idMap;
    this.setState({ creatingColuns: true });
    for (const d of toCreate) {
      const type = Config.COLUMN_TYPE_NAME_MISMATCHES[d.column.type] ?? d.column.type;
      try {
        const res = await mondayApi.api(
          `mutation{
            create_column(board_id: ${board.id}, title:"${d.column.title}", column_type:${type}) {id}
          }`
        );
        idMap[String(d.column.id)] = res.data.create_column.id;
      } catch (e) {
        monday.execute('notice', {
          message:
            'There was an error while attemping to create ' + d.column.title + ' column.',
          type: 'error',
          timeout: Config.NOTIFICATION_TIMEOUT,
        });
      }
    }
    this.setState({ creatingColuns: false });
    return idMap;
  };

  applyTemplate = () => {
    if (templateType(this.selected()) === 'group') {
      this.applyGroup();
      return;
    }
    // applyingTemplate drives a scrim over the template card — NOT `loading`,
    // which would swap the whole page for the skeleton.
    this.setState({ applyingTemplate: true, applyDone: 0, applyTotal: 0, applyResult: null });
    const itemID = this.state.context.itemId;
    Backend.trialStatus(this.state.sessionToken, itemID)
      .then((verdict) => {
        // trialStatus always resolves to { status, message? }: a subscription it
        // could not verify comes back DENIED, matching checkTrial's fail-closed
        // policy (NO_VERDICT in spt/src/helpers/trial.ts). No null case to handle.

        // On a refusal this is why; on a pass it is the >=80% operations warning.
        if (verdict.message) {
          Backend.createNotification(this.state.context.user.id, itemID, verdict.message);
        }

        if (!verdict.status) {
          this.setState({ selectedTemplate: null, applyingTemplate: false });
          return;
        }

        const template = this.selected();

        // returned, so a rejection in here reaches the catch below instead of
        // leaving the scrim up until the user reloads the board
        return this.getSubitemsBoard(itemID).then((board) => {
          if (board == null) {
            this.setState({ applyingTemplate: false });
            return;
          }

          // Resolve columns by normalized title + type (shared matcher, REQ-7.1).
          // All matched -> apply immediately; anything unmatched -> mapping dialog.
          const { idMap, unmatched } = resolveColumns(template.columns, board.columns);
          if (unmatched.length) {
            this.setState({
              applyingTemplate: false,
              applyMapping: {
                template,
                board,
                idMap,
                unmatched,
              },
            });
            return;
          }
          this.executeApply(template, board, idMap, new Set());
        });
      })
      .catch((e) => {
        // Whatever failed, the scrim has to come off: without this the template
        // card stays covered for good and the only way out is a reload.
        console.error('applyTemplate failed', e);
        const name = (this.selected() || {}).name || 'the template';
        this.setState({ applyingTemplate: false, applyResult: applyErrorResult(name, e && e.message ? e.message : String(e)) });
      });
  };

  /**
   * The apply loop — unchanged value composition; columns resolve via idMap,
   * skipIds come from the mapping dialog's "skip" decisions, and any value with
   * no mapping is left out rather than guessed into a column.
   */
  executeApply = async (template, board, idMap, skipIds) => {
    // Dynamic dates and {date} names resolve once, against today in the
    // applier's timezone (lib/dynamicValues), so the loop only sees fixed values.
    const baseDate = applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const subitems = resolveSubitemsForApply(template.subitems, baseDate);
    this.setState({
      applyingTemplate: true,
      applyDone: 0,
      applyTotal: subitems.length,
    });
    const itemID = this.state.context.itemId;
    const failures = [];

    // Updates (requirements F1.10–F1.12) are posted on each subitem right after
    // it is created, as the person applying. The applier and the account's
    // people are read once per apply, and only when the template carries updates.
    const updatesPlanned = countUpdates(subitems);
    const updateCtx = updatesPlanned > 0 ? await this.updateContextFor(template) : null;
    const updateFailures = [];
    let updatesPosted = 0;
    // monday refused create_update for a missing scope: no later update can land
    let updatesRefused = false;

    for (let i = 0; i < subitems.length; i++) {
      let column_values = {};
      for (let k = 0; k < subitems[i].column_values.length; k++) {
        const cv = subitems[i].column_values[k];
        if (!Config.isSupportedColumn(cv.type)) {
          continue;
        }
        if (skipIds.has(String(cv.id))) {
          continue;
        }
        let newColumnID = idMap[String(cv.id)];
        if (newColumnID === undefined) {
          continue; // unmapped — never guess a column
        }
        switch (cv.type) {
          case 'dropdown':
            // Use the text for dropdown to create the new label if needed
            column_values[newColumnID] = cv.text;
            break;
          case 'color':
          case 'status':
            column_values[newColumnID] = {
              label: cv.text,
            };
            break;
          case 'board-relation': {
            const val = JSON.parse(cv.value);
            if (val != null && val.linkedPulseIds != null) {
              const itemIDs = val.linkedPulseIds.map((t) => t.linkedPulseId);
              column_values[newColumnID] = { item_ids: itemIDs };
            }
            break;
          }
          case 'pulse-id':
          case 'pulse-log':
          case 'pulse-updated':
          case 'columns-battery':
            break;
          default:
            // a cell with no value (a date rule that could not resolve) is left out
            if (cv.value !== null && cv.value !== undefined) {
              column_values[newColumnID] = JSON.parse(cv.value);
            }
        }
      }
      let column_values_json = JSON.stringify(JSON.stringify(column_values));
      let name = subitems[i].name;
      try {
        const res = await mondayApi.api(
          `mutation {create_subitem (parent_item_id: ${itemID}, item_name: "${name.replaceAll(
            '"',
            '\\"'
          )}",column_values:${column_values_json}, create_labels_if_missing:true) {id}}`
        );
        // monday reports GraphQL/permission failures as HTTP 200 + errors[] (and
        // lib/mondayApi keeps that envelope), so a bare try/catch never sees them
        // and the row would vanish silently.
        // A rejected value (e.g. a person who is not a board member) fails the
        // WHOLE create_subitem, so the subitem itself is lost — surface it.
        if (res && res.errors) {
          failures.push({ name, errors: res.errors });
          console.error(`create_subitem failed for "${name}"`, res.errors);
        } else if (!(res && res.data && res.data.create_subitem)) {
          failures.push({ name, errors: 'no data returned' });
          console.error(`create_subitem returned no data for "${name}"`, res);
        } else if (updateCtx && !updatesRefused) {
          const posted = await this.postUpdates(
            res.data.create_subitem.id,
            subitems[i],
            updateCtx,
            updateFailures,
          );
          updatesPosted += posted.count;
          updatesRefused = posted.refused;
        }
      } catch (e) {
        failures.push({ name, errors: e && e.message ? e.message : String(e) });
        console.error(`create_subitem threw for "${name}"`, e);
      }
      // after the row settles — success or failure — so the bar reaches 100%
      this.setState({ applyDone: i + 1 });
    }

    // The template stays selected: the alert on its card says how it went,
    // naming each failed row with monday's reason (also logged above).
    this.setState({
      applyingTemplate: false,
      applyResult: subitemApplyResult({
        templateName: template.name,
        total: subitems.length,
        failures,
        updatesPlanned,
        updatesPosted,
        updateFailures,
        updatesRefused,
      }),
    });

    Backend.templateOperation(
      this.state.sessionToken,
      'template_applied',
      template.id
    );
  };

  /**
   * Who applies (for @Applier) and who exists in the account (to drop mentions of
   * people who left, F1.12). Each failure degrades, never blocks: no applier
   * leaves "@Applier" as text; no people list sends every mention and lets
   * monday judge.
   */
  updateContextFor = async (template) => {
    const ctx = { applier: null, known: null, slug: null };
    try {
      const me = await mondayApi.api('query { me { id name } }');
      if (me && me.data && me.data.me) {
        ctx.applier = { id: me.data.me.id, name: me.data.me.name };
      } else {
        console.error('Could not read the applying user for @Applier', me);
      }
    } catch (e) {
      console.error('Could not read the applying user for @Applier', e);
    }
    ctx.slug = await accountSlug(mondayApi);
    const needsPeople = (template.subitems || []).some((s) =>
      (s.updates || []).some((u) => (u.mentions || []).some((m) => m && m.type !== 'Applier')),
    );
    if (needsPeople) {
      const people = await fetchAccountPeople(mondayApi);
      if (!people.error) ctx.known = knownMentionKeys(people);
    }
    return ctx;
  };

  /**
   * Posts one subitem's updates in order (F1.10). A refused update is recorded
   * and the rest still go, except a refusal for a missing scope (the install has
   * not approved updates:write yet): that stops here, unrecorded, and the
   * apply posts no more updates.
   * @returns { count: the number posted, refused: whether the scope was refused }
   */
  postUpdates = async (subitemId, sub, ctx, failures) => {
    let posted = 0;
    for (const u of sub.updates || []) {
      const { body, mentions_list } = serializeUpdate(u, ctx);
      if (!body.trim()) continue;
      try {
        const res = await mondayApi.api(CREATE_UPDATE_MUTATION, {
          variables: { itemId: subitemId, body, mentions: mentions_list },
        });
        if (refusedFields(res).length) {
          console.error(`create_update refused for a missing scope on "${sub.name}"`, res.errors);
          return { count: posted, refused: true };
        }
        if (res && res.errors) {
          failures.push({ name: sub.name, errors: res.errors });
          console.error(`create_update failed on "${sub.name}"`, res.errors);
        } else if (res && res.data && res.data.create_update) {
          posted += 1;
        } else {
          failures.push({ name: sub.name, errors: 'no data returned' });
          console.error(`create_update returned no data on "${sub.name}"`, res);
        }
      } catch (e) {
        failures.push({ name: sub.name, errors: e && e.message ? e.message : String(e) });
        console.error(`create_update threw on "${sub.name}"`, e);
      }
    }
    return { count: posted, refused: false };
  };

  /**
   * Group apply (requirements F2.7–F2.13), from a board view: adds the
   * template's group to this board. The same trial gate as the subitem apply,
   * then the board and its subitems board are read by board id (there is no
   * item to reach them through), columns are matched per board (the mapping dialog
   * gets one section each), and lib/applyGroup runs in the browser with the
   * run card showing progress. A board without a Subitems column is offered
   * an items-only apply instead of a bootstrapped dummy subitem.
   */
  applyGroup = async () => {
    const template = this.selected();
    const boardId = this.state.context.boardId;
    // the trial notice goes to the item in an item view, else to the board
    const noticeTarget = this.state.context.itemId || boardId;
    const show = (patch) => this.setState({ groupRun: { templateName: template.name, ...patch } });
    this.setState({ applyResult: null });
    show({ stage: 'preparing' });
    try {
      const verdict = await Backend.trialStatus(this.state.sessionToken, noticeTarget);
      if (verdict.message) {
        Backend.createNotification(this.state.context.user.id, noticeTarget, verdict.message);
      }
      if (!verdict.status) {
        this.setState({ groupRun: null, selectedTemplate: null });
        return;
      }

      const content = template.content || {};
      const hasSubitems = (content.items || []).some((it) => (it.subitems || []).length > 0);
      const itemBoard = await fetchBoardById(mondayApi, boardId);

      let subBoard = null;
      if (hasSubitems) {
        if (await hasSubitemsColumn(mondayApi, boardId)) {
          subBoard = (await fetchSubitemsBoardOfBoard(mondayApi, boardId)).board;
        } else {
          const res = await monday.execute('confirm', {
            message:
              'This board has no Subitems column, so the template’s subitems cannot be created here. Apply the items only?',
            confirmButton: 'Apply items only',
            cancelButton: 'Cancel',
            excludeCancelButton: false,
          });
          if (!(res && res.data && res.data.confirm)) {
            this.setState({ groupRun: null });
            return;
          }
        }
      }
      const withSubitems = hasSubitems && Boolean(subBoard);

      // Columns match by normalized title + type per board (shared matcher).
      const itemMatch = resolveColumns(content.item_columns || [], itemBoard.columns);
      const subMatch = withSubitems ? resolveColumns(content.subitem_columns || [], subBoard.columns) : { idMap: {}, unmatched: [] };
      const pending = { template, itemBoard, subBoard, withSubitems, itemIdMap: itemMatch.idMap, subIdMap: subMatch.idMap };

      if (itemMatch.unmatched.length || subMatch.unmatched.length) {
        const sections = [
          {
            key: 'item',
            title: 'Item columns · this board',
            unmatched: itemMatch.unmatched,
            boardColumns: itemBoard.columns,
            usedBoardIds: Object.values(itemMatch.idMap),
          },
        ];
        if (withSubitems) {
          sections.push({
            key: 'subitem',
            title: 'Subitem columns · subitems board',
            unmatched: subMatch.unmatched,
            boardColumns: subBoard.columns,
            usedBoardIds: Object.values(subMatch.idMap),
          });
        }
        this.setState({ groupRun: null, applyMapping: { kind: 'group', pending, sections } });
        return;
      }

      await this.runGroupApply(pending, new Set(), new Set());
    } catch (e) {
      console.error('applyGroup failed', e);
      this.setState({ groupRun: null, applyResult: applyErrorResult(template.name, e && e.message ? e.message : String(e)) });
    }
  };

  /** The run itself, after mapping; progress shows in the apply overlay, the result in the alert; billed once. */
  runGroupApply = async (pending, itemSkipIds, subSkipIds) => {
    const { template, withSubitems, itemIdMap, subIdMap } = pending;
    const show = (patch) => this.setState({ groupRun: { templateName: template.name, ...patch } });
    try {
      const result = await applyGroupTemplate(mondayApi, {
        content: template.content,
        boardId: this.state.context.boardId,
        mapping: { itemIdMap, itemSkipIds, subIdMap, subSkipIds },
        withSubitems,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // The run is done when the result is in hand, not when the last
        // progress tick says so: a 'done' tick without a result crashed the card.
        onProgress: (p) => {
          if (p.stage !== 'done') show(p);
        },
      });
      // the template stays selected: the alert on its card says how it went
      this.setState({ groupRun: null, applyResult: groupApplyResult(template.name, result) });
      Backend.templateOperation(this.state.sessionToken, 'template_applied', template.id);
    } catch (e) {
      console.error('runGroupApply failed', e);
      this.setState({ groupRun: null, applyResult: applyErrorResult(template.name, e && e.message ? e.message : String(e)) });
    }
  };

  /** Mapping dialog confirmed — extend the idMap per decision, heal the template if asked. */
  handleMappingConfirm = async (decisions) => {
    if (this.state.applyMapping.kind === 'group') {
      const { pending } = this.state.applyMapping;
      this.setState({ applyMapping: null, groupRun: { templateName: pending.template.name, stage: 'preparing' } });
      const itemSkipIds = new Set();
      const subSkipIds = new Set();
      for (const d of decisions) {
        const map = d.section === 'subitem' ? pending.subIdMap : pending.itemIdMap;
        const skips = d.section === 'subitem' ? subSkipIds : itemSkipIds;
        if (d.action === 'map' && d.targetId !== undefined) map[String(d.column.id)] = d.targetId;
        else if (d.action === 'skip') skips.add(String(d.column.id));
      }
      await this.createColumnsForDecisions(pending.itemBoard, decisions.filter((d) => d.section !== 'subitem'), pending.itemIdMap);
      if (pending.withSubitems) {
        await this.createColumnsForDecisions(pending.subBoard, decisions.filter((d) => d.section === 'subitem'), pending.subIdMap);
      }
      await this.runGroupApply(pending, itemSkipIds, subSkipIds);
      return;
    }

    const { template, board, idMap } = this.state.applyMapping;
    this.setState({ applyMapping: null });

    // Map/skip decisions resolve THIS apply only. The template is deliberately
    // never rewritten here — the dialog has no "remember" option, so silently
    // renaming a stored column would be an edit the user never agreed to. The
    // dialog warns instead that it will reappear until one side is renamed.
    const skipIds = new Set();
    for (const d of decisions) {
      if (d.action === 'map' && d.targetId !== undefined) {
        idMap[String(d.column.id)] = d.targetId;
      } else if (d.action === 'skip') {
        skipIds.add(String(d.column.id));
      }
    }
    const extendedMap = await this.createColumnsForDecisions(board, decisions, idMap);

    this.executeApply(template, board, extendedMap, skipIds);
  };

  handleMappingCancel = () => {
    this.setState({ applyMapping: null });
  };

  deleteTemplate = () => {
    monday
      .execute('confirm', {
        message: 'Are you sure you want to delete this template?',
        confirmButton: 'Yes, delete it!',
        cancelButton: 'Cancel',
        excludeCancelButton: false,
      })
      .then((res) => {
        if (res.data.confirm) {
          // A one-row DELETE does not deserve a page takeover: spin the Delete
          // button and disable its siblings so the list stays on screen.
          this.setState({ deletingTemplate: true });

          let templateID =
            this.selected().id;

          Backend.deleteTemplate(this.state.sessionToken, templateID)
            .then((templates) => {
              this.setState({
                templates,
                deletingTemplate: false,
                selectedTemplate: null,
              });
            })
            .catch((e) => {
              // Without this the spinner would never stop and the row would look
              // permanently mid-delete.
              console.error('deleteTemplate failed', e);
              this.setState({ deletingTemplate: false });
            });
        }
      });
  };

  templateChanged = (val) => {
    this.setState({ selectedTemplate: val, applyResult: null });
  };

  /**
   * A full-bleed message that still lives inside the shell — used for the two
   * states where the app cannot proceed at all.
   */
  renderBlockingMessage = ({ tone, title, body, note }) => {
    const ring = tone === 'danger'
      ? { bubble: 'bg-danger-disc', icon: 'text-danger-ink', box: 'border-danger bg-danger-tint', text: 'text-danger-ink' }
      : { bubble: 'bg-warning-disc', icon: 'text-warning-ink', box: 'border-warning bg-warning-tint', text: 'text-warning-ink' };
    return (
      <div className="flex items-center justify-center min-h-[360px]">
        <div className="max-w-md px-6 mx-auto text-center">
          <div className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${ring.bubble}`}>
            <AlertCircle className={`w-8 h-8 ${ring.icon}`} />
          </div>
          <h2 className="mb-4 text-2xl font-bold text-primary">{title}</h2>
          <p className="mb-6 text-secondary">{body}</p>
          <div className={`p-4 border rounded-lg ${ring.box}`}>
            <p className={`text-sm ${ring.text}`}>{note}</p>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Progress for a running apply, as a SCRIM over the template card rather than a
   * page of its own — navbar, heading, selector and the template all stay put
   * (design decision 2026-08-19, mockup option 4).
   *
   * bg-surface/95 + backdrop-blur-sm, not a light wash: at 78% the table stayed
   * legible through the text and the whole thing read as a rendering glitch
   * rather than a deliberate state.
   *
   * z-10 is REQUIRED, not decoration. SubitemsTable's sticky first column sets
   * `position: sticky; z-index: 3` on its cells and 5 on its header (see
   * SubitemsTable.css), and a positioned element with a z-index paints above an
   * `z-index: auto` sibling regardless of DOM order — so without this the row
   * numbers and subitem names punched straight through the scrim.
   */
  renderApplyScrim = () => {
    const { applyDone, applyTotal, creatingColuns, groupRun } = this.state;
    // A group apply reports its own stages (group, items, subitems, updates).
    const group = groupRunActive(groupRun) ? groupProgressText(groupRun) : null;
    // No total yet = still checking the trial and resolving the board, so there is
    // nothing honest to put on the bar. Leave it empty rather than fake movement.
    const pct = group ? group.pct : applyTotal ? Math.round((applyDone / applyTotal) * 100) : 0;
    return (
      <div
        className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-surface/95 backdrop-blur-sm"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="px-6 text-center" style={{ minWidth: 260 }}>
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-accent-ink animate-spin" />
          <p className="mb-3 font-semibold text-primary">Applying template</p>
          <div className="w-full h-1.5 bg-track rounded-full">
            <div
              className="h-1.5 transition-all duration-300 bg-accent rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-secondary">
            {creatingColuns
              ? 'Creating missing columns…'
              : group
              ? group.text
              : applyTotal
              ? `${applyDone} of ${applyTotal} subitems`
              : 'Preparing…'}
          </p>
        </div>
      </div>
    );
  };

  /**
   * Every state of the app, as CONTENT — never a page. Ordered by precedence:
   * hard blocks first, then loading, then the editor, then the list. render()
   * below supplies the one shell they all sit in, so the navbar survives all of
   * them (it used to be destroyed by five separate early returns).
   */
  renderContent = () => {
    if (this.state.context.user && this.state.context.user.isViewOnly) {
      return this.renderBlockingMessage({
        tone: 'danger',
        title: 'Unauthorized Access',
        body: 'As a viewer, you are unable to use the Subitem Pro Templates app.',
        note: 'Please contact your administrator for access permissions.',
      });
    }

    /*
     * Above noBoardError and `loading` on purpose: neither has anything to do
     * with the account's usage dashboard, and a user who lands on a board with
     * no subitems board should still be able to read their operation counter.
     * Below the view-only block, which is a refusal of the whole app.
     */
    if (this.state.usageOpen) {
      // `account.id` is the documented shape; the flat `accountId` is what some
      // older monday contexts hand back, and either one resolves the same link.
      const { context } = this.state;
      return (
        <AppUsageView
          accountId={context?.account?.id ?? context?.accountId}
          onBack={this.closeUsage}
        />
      );
    }

    if (this.state.noBoardError) {
      return this.renderBlockingMessage({
        tone: 'warning',
        title: 'No Subitems Board',
        body: 'This board has not been initialized for subitems. Create a subitem via the UI first to proceed.',
        note: 'Initialize the board by creating your first subitem through the Monday.com interface.',
      });
    }

    // First load: waiting on sessionToken + GET /templates, and on the context
    // that says which view this is (and so which kind to list).
    if (this.state.loading || !this.state.contextReady) return <LayoutSkeleton variant="list" label="Loading templates…" />;

    if (this.state.editorOpen) {
      const Editor = this.state.editorKind === 'group' ? GroupTemplateEditor : TemplateEditor;
      return (
        <Editor
          monday={mondayApi} /* the editors only call .api(); host calls stay here */
          itemId={this.state.context.itemId}
          boardId={this.state.context.boardId} /* the group editor captures from this board */
          template={this.state.editorTemplate}
          takenNames={takenNamesFor(
            this.state.templates,
            this.state.editorKind,
            this.state.editorTemplate && this.state.editorTemplate.id,
          )}
          onSave={this.handleEditorSave}
          onCancel={this.handleEditorCancel}
        />
      );
    }

    const tab = TEMPLATE_KINDS.find((t) => t.kind === this.templateKind()) || TEMPLATE_KINDS[0];

    return (
      <>
              {/*
                Same rhythm as the editor: page heading + subheading at page level
                (no card), then the content. The selector is deliberately NOT in a
                card — a card around one control is mostly empty box, and only real
                content earns an elevation here.
              */}
              <div className="mb-6">
                <h1 className="text-xl font-bold text-primary">{tab.label}</h1>
                <p className="mt-2 text-sm text-secondary">{tab.hint}</p>
              </div>

              <div className="relative z-20 max-w-md mb-6" data-tour="picker">
                <label className="block mb-2 text-sm font-medium text-primary">Available Templates</label>
                <Dropdown
                  className="dropdown-stories-styles_spacing"
                  onChange={this.templateChanged}
                  options={this.visibleTemplates().map((template, index) => {
                    return { label: template.name, value: index, author: authorNameOf(template, this.state.authorNames) };
                  })}
                  optionRenderer={this.renderTemplateOption}
                  placeholder="Select a template"
                  value={this.state.selectedTemplate}
                />
              </div>

              {this.state.selectedTemplate !== null ? (
                this.renderTemplateSelected()
              ) : (
                <div className="p-8 mb-6 text-center bg-surface border-2 border-subtle border-dashed rounded-lg">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-sunken rounded-full">
                    <FolderOpen className="w-6 h-6 text-secondary/70" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-primary">No template selected</h3>
                  <p className="mb-6 text-secondary">Select a template from the dropdown above or create a new one to get started.</p>
                  <button
                    onClick={this.createNewTemplate}
                    data-tour="create"
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-on-accent bg-accent border border-transparent rounded-md shadow-sm hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Template
                  </button>
                </div>
              )}
      </>
    );
  };

  /**
   * The ONE page shell. Navbar plus the content column, and nothing else — every
   * state renders into it via renderContent(), so the header is never replaced.
   * This is the only place min-height, padding and background are set (no max-width:
   * the content is full width, like the navbar);
   * TemplateEditor and LayoutSkeleton deliberately carry no page chrome.
   */
  render() {
    return (
      <div className="min-h-screen bg-page">
        {this.renderNavbar()}

        {/* full width: the content tracks the iframe, like the navbar */}
        <div className="px-6 py-6">{this.renderContent()}</div>

        {/*
          Rendered at page level rather than inside renderContent() so it overlays
          the content instead of occupying a slot in it. Deliberately hidden while
          the editor is open — nothing should float over an unsaved form — and while
          the apply progress or a blocking message owns the screen.
        */}
        {this.showHelpBubble() && this.renderHelpBubble()}

        {this.state.applyMapping && this.state.applyMapping.kind === 'group' && (
          <ApplyMappingDialog
            show
            sections={this.state.applyMapping.sections}
            onConfirm={this.handleMappingConfirm}
            onCancel={this.handleMappingCancel}
          />
        )}
        {this.state.applyMapping && this.state.applyMapping.kind !== 'group' && (
          <ApplyMappingDialog
            show
            unmatched={this.state.applyMapping.unmatched}
            boardColumns={this.state.applyMapping.board.columns}
            usedBoardIds={Object.values(this.state.applyMapping.idMap)}
            onConfirm={this.handleMappingConfirm}
            onCancel={this.handleMappingCancel}
          />
        )}
      </div>
    );
  }
}

export default App;
